import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import LoadingSpinner from '../LoadingSpinner';
import ErrorText from '../ErrorText';
import Button from '../inputs/Button';
import CheckPaymentMethodStatus from './CheckPaymentMethodStatus';
import ExternalThemeLink from '../ExternalThemeLink';
import Half from '../layout/Half';
import { useOrder } from '../../hooks/useOrder';
import SelectPaymentMethod from './SelectPaymentMethod';
import InsetLoadingOverlay from '../InsetLoadingOverlay';
import PromoCodeForOrders from './PromoCodeForOrders';
import { currency } from '../../utils';
import CompletePurchaseButton from '../payments/CompletePurchaseButton';
import { getProduct } from '../../helpers/products';
import { useAuth } from '../../hooks/useAuth';
import { isStripePaymentRequired } from '../../utils/featureFlags';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PK;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

export default function OrderPage({
  productLabel,
  productPricing,
  productDetails,
  orderSummary,
  backButton,
  showTotal = true,
  title = 'Order Summary',
  detailsPosition = 'right',
  // Optional second complete button ({ label, variables }) that completes
  // the order with extra mutation variables merged in — e.g. the audience
  // order page's "Complete and Profile".
  secondaryComplete = null,
}) {
  const { state, dispatch, loading, error, completeOrder } = useOrder();
  const {
    loadingCustomer,
    loadingCompleteOrder,
    loadingApplyPromoCode,
    loadingAddOnToOrder,
    loadingRemoveFromOrder,
    loadingGetOrder,
    totalPrice,
    orderDetails,
    paymentMethod,
    products,
    errorOpenOrder,
  } = state;
  const { orderId, coreProductName } = orderDetails;

  const { user } = useAuth();
  const { plan } = user;
  const navigate = useNavigate();
  // Set only when a click arrives with no order behind it. Rendered through the
  // same ErrorText as the order errors below.
  const [clickError, setClickError] = useState<string | null>(null);

  function handleClickCompletePurchase(extraVariables = {}) {
    // An order page rendered without an orderId is a bug in its own right —
    // most likely a slow or failed `openOrder`. This used to `return` silently:
    // the button looked live, the click did nothing, no spinner, no request,
    // and users fell back to plain "Complete", which skipped the chained
    // profile order and left the audience with no export to crawl.
    if (!orderId) {
      console.error(
        'Complete purchase clicked with no orderId on the order page; ' +
          'the order was never opened.'
      );
      setClickError(
        "We couldn't find your order. Please go back and try again."
      );
      return;
    }

    setClickError(null);

    const includes = {
      includeEmail: {
        productKey: 'emailAddOn',
        include: false,
      },
      includeIP: {
        productKey: 'ipAddOn',
        include: false,
      },
      includePhone: {
        productKey: 'phoneAddOn',
        include: false,
      },
    };

    if (coreProductName === 'audience') {
      if (
        products.find(
          (p) => p.id === getProduct('identifierBundleAddOn', plan).id
        )
      ) {
        Object.keys(includes).forEach((key) => (includes[key].include = true));
      } else {
        Object.entries(includes).forEach(([key, { productKey }]) => {
          const { id: addOnProductId } = getProduct(productKey, plan);
          const foundInclude = products.find((p) => p.id === addOnProductId);
          includes[key].include = !!foundInclude;
        });
      }
    }

    // Get distribution configurations from session storage
    const distributionConfigsJson = sessionStorage.getItem('distributionConfigs');
    const distributionConfigs = distributionConfigsJson ? JSON.parse(distributionConfigsJson) : null;

    const variables = {
      orderId,
      pmId: paymentMethod?.id || 'bypass',
      ...Object.entries(includes).reduce((accObj, [key, { include }]) => {
        accObj[key] = include;
        return accObj;
      }, {}),
      ...extraVariables,
    };

    // Add distribution configurations if they exist
    if (distributionConfigs && distributionConfigs.length > 0) {
      variables.distributionConfigs = distributionConfigs;
    }

    completeOrder({ variables });

    // Clear distribution configurations from session storage after use
    sessionStorage.removeItem('distributionConfigs');
  }

  return (
    <div className="relative w-full h-full flex flex-col overflow-auto py-3 px-6">
      {loading && <InsetLoadingOverlay message="Setting up your order..." />}
      {loadingCompleteOrder && (
        <InsetLoadingOverlay message="Finalizing your order..." />
      )}
      {loadingGetOrder && (
        <InsetLoadingOverlay message="Retrieving your order..." />
      )}
      <h3>{title}</h3>
      {productLabel && <div className="mt-4">{productLabel}</div>}
      {orderSummary && <div className="my-6">{orderSummary}</div>}
      {loadingCustomer && (
        <LoadingSpinner message="Getting existing payment methods..." />
      )}
      <div className="flex flex-row py-6">
        <Half>
          {detailsPosition === 'left' &&
            !loadingCompleteOrder &&
            !error &&
            productDetails}
          <div className={detailsPosition === 'left' ? 'mt-8' : ''}>
            <SelectPaymentMethod state={state} dispatch={dispatch} />
          </div>
          {productPricing && (
            <div className={`py-8 space-y-1`}>{productPricing}</div>
          )}
          {showTotal && (
            <div className="flex flex-row pb-7 space-x-2">
              <span className="text-xl font-light">
                Total Price for Order:{' '}
              </span>
              <span className="text-lg font-bold"> {currency(totalPrice)}</span>
            </div>
          )}
          <PromoCodeForOrders
            dispatch={dispatch}
            className={`pb-10 ${!showTotal && !productPricing ? 'py-7' : ''}`}
          />
          {/* Stacked, not laid out in a row. The buttons cannot shrink
              (Button applies whitespace-nowrap + px-8 + min-w-52) and measure
              295 + 211 + 256px, so a row needs 786px inside a column that is
              half the page width — ~1620px of page width. Below that the last
              button (always "Complete and Profile") overflowed into the
              right-hand Half, which paints over it as a later sibling: covered
              AND unclickable, while "Complete Order" stayed put. A user on a
              1440px screen never knew the profile option existed.

              Source order is the visual order on purpose — no flex-*-reverse —
              so keyboard tab order matches what is on screen. The stack is
              capped at 20rem (max-w-xs; this Tailwind 3.4 config has no
              spacing-scale max-w-*) and stretched to it, so the three buttons
              share one width instead of forming a ragged edge. Every label in
              use fits: the widest is "Back to Saved Audiences" at 295px. */}
          <div className="flex flex-col items-stretch gap-3 max-w-xs">
            {secondaryComplete && (
              <Button
                type="button"
                primary
                className="min-w-max"
                disabled={
                  loadingCompleteOrder ||
                  loadingApplyPromoCode ||
                  loadingAddOnToOrder ||
                  loadingRemoveFromOrder ||
                  (!isStripePaymentRequired() ? false : !paymentMethod?.id) ||
                  errorOpenOrder ||
                  // "Complete and Profile" is the button this incident started
                  // on: it dropped the click, the user fell back to plain
                  // Complete, and the profile order was never chained.
                  !orderId
                }
                onClick={() =>
                  handleClickCompletePurchase(secondaryComplete.variables)
                }
              >
                {secondaryComplete.label}
              </Button>
            )}
            <CompletePurchaseButton
              disabled={
                loadingCompleteOrder ||
                loadingApplyPromoCode ||
                loadingAddOnToOrder ||
                loadingRemoveFromOrder ||
                (!isStripePaymentRequired() ? false : !paymentMethod?.id) ||
                errorOpenOrder ||
                // Visibly disabled rather than inert while the order is still
                // opening (or failed to open).
                !orderId
              }
              onClick={() => handleClickCompletePurchase()}
            />
            {backButton}
            {!backButton && (
              <Button
                type="button"
                secondary
                small
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Back
              </Button>
            )}
          </div>
        </Half>
        <Half>
          <>
            {detailsPosition === 'right' &&
              !loadingCompleteOrder &&
              !error &&
              productDetails}
            {loadingCompleteOrder && (
              <InsetLoadingOverlay message="Completing your purchase..." />
            )}
            {(error || clickError) && (
              <ErrorText message={error?.message || clickError} />
            )}
          </>
        </Half>
      </div>
      {isStripePaymentRequired() && stripePromise && (
        <Elements stripe={stripePromise}>
          <CheckPaymentMethodStatus />
        </Elements>
      )}
    </div>
  );
}
